import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  EnterpriseCategory,
  EnterpriseCategoryCreatedEnvelope,
  EnterpriseCategoryDeletedEnvelope,
  EnterpriseCategoryTree,
  EnterpriseCategoryUpdatedEnvelope,
  EnterpriseOnboardingOptions,
  EnterpriseOnboardingResponse,
  GetCategoryOptions,
  GetMediaManagerCategoriesResponse,
  UpdateCategory,
  createCategory as universalCreateCategory,
  deleteCategory as universalDeleteCategory,
  enterpriseOnboarding as universalEnterpriseOnboarding,
  getCategory as universalGetCategory,
  getMediaManagerCategories as universalGetMediaManagerCategories,
  updateCategory as universalUpdateCategory,
} from './media-enterprise-public-media-v1-enterprise-category-enterprise-media-categories.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/media' };

export function createCategory(
  httpClient: HttpClient
): CreateCategorySignature {
  return (category: NonNullablePaths<EnterpriseCategory, `displayName`, 2>) =>
    universalCreateCategory(
      category,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCategorySignature {
  /**
   * Creates a category under the account's master category. Only one level of categories is supported under the master category. Before creating categories, ensure [Enterprise Onboarding](#enterpriseonboarding) has been called to initialize the account's root category structure.
   *
   * To delete a category, call [Delete Category](#deletecategory).
   * @param - Category to create.
   * @returns Created category.
   */
  (category: NonNullablePaths<EnterpriseCategory, `displayName`, 2>): Promise<
    NonNullablePaths<EnterpriseCategory, `_id` | `publishStatus`, 2>
  >;
}

export function deleteCategory(
  httpClient: HttpClient
): DeleteCategorySignature {
  return (categoryId: string) =>
    universalDeleteCategory(
      categoryId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCategorySignature {
  /**
   * Deletes a category including all its subcategories. Items in the category are not deleted.
   *
   * To create a category, call [Create Category](#createcategory).
   * @param - Category ID.
   */
  (categoryId: string): Promise<void>;
}

export function updateCategory(
  httpClient: HttpClient
): UpdateCategorySignature {
  return (_id: string, category: UpdateCategory) =>
    universalUpdateCategory(
      _id,
      category,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCategorySignature {
  /**
   * Updates a category.
   * @param - Category ID.
   * @returns Updated category.
   */
  (_id: string, category: UpdateCategory): Promise<
    NonNullablePaths<EnterpriseCategory, `_id` | `publishStatus`, 2>
  >;
}

export function getCategory(httpClient: HttpClient): GetCategorySignature {
  return (categoryId: string, options?: GetCategoryOptions) =>
    universalGetCategory(
      categoryId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCategorySignature {
  /**
   * Retrieves a category by ID, optionally including subcategories.
   * @param - Category ID.
   * @returns Category details including subcategories if requested.
   */
  (categoryId: string, options?: GetCategoryOptions): Promise<
    NonNullablePaths<
      EnterpriseCategoryTree,
      `category._id` | `category.publishStatus` | `subCategories`,
      3
    >
  >;
}

export function enterpriseOnboarding(
  httpClient: HttpClient
): EnterpriseOnboardingSignature {
  return (accountId: string, options?: EnterpriseOnboardingOptions) =>
    universalEnterpriseOnboarding(
      accountId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface EnterpriseOnboardingSignature {
  /**
   * Initializes the enterprise category structure for an organization during first-time setup. Creates the root account category using the provided account information.
   * If the root account category already exists, this method returns the existing category structure without creating duplicates.
   *
   * The caller's account ID must match the `accountId` in the request.
   * @param - Account ID of the organization. Used as the organization category ID.
   */
  (accountId: string, options?: EnterpriseOnboardingOptions): Promise<
    NonNullablePaths<
      EnterpriseOnboardingResponse,
      `category._id` | `category.publishStatus`,
      3
    >
  >;
}

export function getMediaManagerCategories(
  httpClient: HttpClient
): GetMediaManagerCategoriesSignature {
  return () =>
    universalGetMediaManagerCategories(
      // @ts-ignore
      { httpClient }
    );
}

interface GetMediaManagerCategoriesSignature {
  /**
   * Retrieves the category tree for the media manager, including all categories accessible to the current user.
   */
  (): Promise<
    NonNullablePaths<
      GetMediaManagerCategoriesResponse,
      | `category.category._id`
      | `category.category.publishStatus`
      | `category.subCategories`,
      4
    >
  >;
}

export const onEnterpriseCategoryCreated = EventDefinition(
  'wix.media.enterprise_public_media.v1.enterprise_category_created',
  true,
  (event: EnterpriseCategoryCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.sortOrder' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EnterpriseCategoryCreatedEnvelope>();
export const onEnterpriseCategoryDeleted = EventDefinition(
  'wix.media.enterprise_public_media.v1.enterprise_category_deleted',
  true,
  (event: EnterpriseCategoryDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.sortOrder' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EnterpriseCategoryDeletedEnvelope>();
export const onEnterpriseCategoryUpdated = EventDefinition(
  'wix.media.enterprise_public_media.v1.enterprise_category_updated',
  true,
  (event: EnterpriseCategoryUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.sortOrder' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EnterpriseCategoryUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CreateCategoryRequest,
  CreateCategoryResponse,
  DeleteCategoryRequest,
  DeleteCategoryResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EnterpriseCategory,
  EnterpriseCategoryCreatedEnvelope,
  EnterpriseCategoryDeletedEnvelope,
  EnterpriseCategoryTree,
  EnterpriseCategoryUpdatedEnvelope,
  EnterpriseOnboardingOptions,
  EnterpriseOnboardingRequest,
  EnterpriseOnboardingResponse,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetCategoryOptions,
  GetCategoryRequest,
  GetCategoryResponse,
  GetMediaManagerCategoriesRequest,
  GetMediaManagerCategoriesResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  LinkItemsToCategoryRequest,
  LinkItemsToCategoryResponse,
  MediaType,
  MessageEnvelope,
  PublishStatus,
  RestoreInfo,
  UnlinkItemsFromCategoryRequest,
  UnlinkItemsFromCategoryResponse,
  UpdateCategory,
  UpdateCategoryRequest,
  UpdateCategoryResponse,
  WebhookIdentityType,
} from './media-enterprise-public-media-v1-enterprise-category-enterprise-media-categories.universal.js';
